import { useState } from 'react';
import { Button, DataTable, EmptyState, Pagination, Panel, SearchInput, SectionHeader, SelectInput, useClientTable, type DataTableSort } from '@bcecloud/portal-ui';
import { DISQUALIFY_LABEL, DISQUALIFY_REASON, receivedLabel, type DisqualifyReason, type LeadPanel, type PanelLead } from '@/lib/lead-panel';
import { EventCell, LeadCell, SalesforceLink } from './LeadCell';

const QUEUE_SORT_KEYS = ['createdAt', 'company', 'leadSource'] as const;
const DEFAULT_SORT: DataTableSort = { key: 'createdAt', direction: 'desc' };

export interface QueueTableProps {
  /** The section heading and its line underneath; the search sits beside them. */
  readonly title: string;
  readonly description: string;
  readonly panel: LeadPanel;
  /** Sales reps who can receive a lead, in name order. */
  readonly reps: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly pending: boolean;
  readonly onAssign: (lead: PanelLead, ownerId: string) => void;
  readonly onDisqualify: (lead: PanelLead, reason: DisqualifyReason) => void;
}

/**
 * The leads no rep owns yet. With the manage_lead_assignment grant each row
 * gets a rep picker and the two disqualify actions; everyone else sees the
 * queue read-only.
 */
export function QueueTable({ title, description, panel, reps, pending, onAssign, onDisqualify }: QueueTableProps): React.ReactElement {
  const [choice, setChoice] = useState<Readonly<Record<string, string>>>({});
  const table = useClientTable<PanelLead>({
    rows: panel.queue,
    searchText: (lead) => [lead.company, lead.contactName, lead.email, lead.eventCity, lead.program, lead.leadSource].join(' '),
    sortKeys: QUEUE_SORT_KEYS,
    defaultSort: DEFAULT_SORT,
    prefix: 'queue',
  });

  if (panel.queue.length === 0) {
    return (
      <>
        <SectionHeader title={title} description={description} />
        <Panel>
        <EmptyState
          title="No leads waiting"
          message="New leads land here until they're given to a rep. Spam you mark leaves the queue."
        />
        </Panel>
      </>
    );
  }

  return (
    <>
      <SectionHeader
        title={title}
        description={description}
        aside={
          <SearchInput
            label="Search waiting leads"
            placeholder="Company, contact, city or program"
            value={table.search}
            onChange={(event) => table.setSearch(event.target.value)}
            className="w-96 max-w-full"
          />
        }
      />
      <DataTable
        headers={[
          { label: 'Received', sortKey: 'createdAt' },
          { label: 'Lead', sortKey: 'company' },
          'Event',
          { label: 'Source', sortKey: 'leadSource' },
          'Actions',
        ]}
        visuallyHiddenHeaders={[4]}
        sort={table.sort}
        onSort={table.onSort}
        minWidth={980}
      >
        {table.pageRows.map((lead) => {
          const ownerId = choice[lead.id] ?? '';
          return (
            <tr key={lead.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{receivedLabel(lead.createdAt, panel)}</td>
              <td className="min-w-64 p-3 text-portal-small">
                <LeadCell lead={lead} />
              </td>
              <td className="p-3 text-portal-small">
                <EventCell lead={lead} />
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{lead.leadSource ?? 'Unknown'}</td>
              <td className="p-3">
                <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                  {panel.canManage ? (
                    <>
                      <SelectInput
                        density="compact"
                        aria-label={`Rep for ${lead.company || lead.contactName}`}
                        value={ownerId}
                        onChange={(event) => setChoice({ ...choice, [lead.id]: event.target.value })}
                        className="min-w-44"
                      >
                        <option value="">Choose a rep</option>
                        {reps.map((rep) => (
                          <option key={rep.id} value={rep.id}>
                            {rep.name}
                          </option>
                        ))}
                      </SelectInput>
                      <Button size="compact" disabled={!ownerId || pending} onClick={() => onAssign(lead, ownerId)}>
                        Assign
                      </Button>
                      {[DISQUALIFY_REASON.spam, DISQUALIFY_REASON.unqualified].map((reason) => (
                        <Button
                          key={reason}
                          size="compact"
                          variant="ghost"
                          disabled={pending}
                          onClick={() => onDisqualify(lead, reason)}
                        >
                          {DISQUALIFY_LABEL[reason].verb}
                        </Button>
                      ))}
                    </>
                  ) : null}
                  <SalesforceLink lead={lead} />
                </div>
              </td>
            </tr>
          );
        })}
      </DataTable>
      <Pagination page={table.page} totalPages={table.totalPages} onChange={table.setPage} summary={table.summary} className="mt-3" />
    </>
  );
}
